import { computeScaledDimensions } from '../resize/resize-image';
import { ResizeOptions } from '../model';

declare global {
  interface MediaTrackCapabilities {
    torch?: boolean;
  }
  interface MediaTrackConstraintSet {
    torch?: boolean;
  }
}

export const TORCH_WARMUP_MS = 400;

export const REAR_CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: { ideal: 'environment' }, width: { ideal: 4032 }, height: { ideal: 3024 } }
};

export interface PhotoCapture {
  takePhoto(): Promise<Blob>;
}

type PhotoCaptureConstructor = new (track: MediaStreamTrack) => PhotoCapture;

export const createPhotoCapture = (track: MediaStreamTrack): PhotoCapture | null => {
  const ImageCaptureApi = (window as unknown as { ImageCapture?: PhotoCaptureConstructor }).ImageCapture;
  if (!ImageCaptureApi) {
    return null;
  }
  try {
    const photoCapture = new ImageCaptureApi(track);
    return typeof photoCapture.takePhoto === 'function' ? photoCapture : null;
  } catch {
    return null;
  }
};

export const supportsTorch = (track: MediaStreamTrack): boolean => {
  if (typeof track.getCapabilities !== 'function') {
    return false;
  }
  return track.getCapabilities().torch === true;
};

export const setTorch = async (track: MediaStreamTrack, on: boolean): Promise<void> => {
  await track.applyConstraints({ advanced: [{ torch: on }] });
};

export const TORCH_CONFIRM_ATTEMPTS = 3;
export const TORCH_CONFIRM_DELAY_MS = 300;

const torchSetting = (track: MediaStreamTrack): boolean | undefined =>
  typeof track.getSettings === 'function' ? (track.getSettings() as { torch?: boolean }).torch : undefined;

const wait = (ms: number): Promise<void> => new Promise(resolve => window.setTimeout(resolve, ms));

export const keepTorchOn = async (
  track: MediaStreamTrack,
  attempts = TORCH_CONFIRM_ATTEMPTS,
  delayMs = TORCH_CONFIRM_DELAY_MS
): Promise<void> => {
  for (let attempt = 0; attempt < attempts; attempt++) {
    await wait(delayMs);
    if (torchSetting(track) !== false) {
      return;
    }
    await setTorch(track, true);
  }
};

export const isDevicePortrait = (): boolean =>
  screen.orientation?.type?.startsWith('portrait') ?? window.innerHeight >= window.innerWidth;

export const CAMERA_READY_TIMEOUT_MS = 10_000;

export const waitForCameraReady = (
  video: HTMLVideoElement,
  warmUpMs = TORCH_WARMUP_MS,
  timeoutMs = CAMERA_READY_TIMEOUT_MS
): Promise<boolean> =>
  new Promise(resolve => {
    let settled = false;
    const finish = (ready: boolean): void => {
      if (!settled) {
        settled = true;
        resolve(ready);
      }
    };

    const timeout = window.setTimeout(() => finish(false), timeoutMs);
    const settle = (): number =>
      window.setTimeout(() => {
        window.clearTimeout(timeout);
        finish(true);
      }, warmUpMs);

    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      settle();
      return;
    }
    video.addEventListener('loadeddata', () => settle(), { once: true });
  });

export interface FrameLayout {
  readonly drawWidth: number;
  readonly drawHeight: number;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly rotateClockwise: boolean;
}

export const computeFrameLayout = (
  videoWidth: number,
  videoHeight: number,
  maxEdgePx: number,
  devicePortrait = true
): FrameLayout => {
  const { width, height } = computeScaledDimensions(videoWidth, videoHeight, maxEdgePx);
  const rotateClockwise = videoWidth > videoHeight && devicePortrait;
  return {
    drawWidth: width,
    drawHeight: height,
    canvasWidth: rotateClockwise ? height : width,
    canvasHeight: rotateClockwise ? width : height,
    rotateClockwise
  };
};

export const grabFrame = (video: HTMLVideoElement, options: ResizeOptions, devicePortrait = true): string => {
  const layout = computeFrameLayout(video.videoWidth, video.videoHeight, options.maxEdgePx, devicePortrait);
  const canvas = document.createElement('canvas');
  canvas.width = layout.canvasWidth;
  canvas.height = layout.canvasHeight;
  const context = canvas.getContext('2d');
  if (context === null) {
    throw new Error('canvas 2d context unavailable');
  }
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  if (layout.rotateClockwise) {
    context.translate(canvas.width, 0);
    context.rotate(Math.PI / 2);
  }
  context.drawImage(video, 0, 0, layout.drawWidth, layout.drawHeight);
  return canvas.toDataURL('image/jpeg', options.quality);
};

export const closeCamera = (stream: MediaStream): void => {
  for (const track of stream.getVideoTracks()) {
    if (supportsTorch(track)) {
      void setTorch(track, false).catch(() => undefined);
    }
    track.stop();
  }
};
